import { createFeatureSelector, createSelector } from '@ngrx/store';
import { UserRole } from '@cpd-app/shared-module/constants';
import { UserActionEnum } from '@cpd-app/generated';
import { Member } from '@cpd-app/core-module/models';
import { environment } from '@cpd-app/environment';

import * as AuthFeatureState from './auth.state';

export const selectAuthState =
  createFeatureSelector<AuthFeatureState.AuthState>(
    AuthFeatureState.authFeatureKey
  );

// AUTH TOKEN
export const selectAuthToken = createSelector(
  selectAuthState,
  (auth) => auth.token
);

// AUTH SHARED_TOKEN
export const selectAuthSharedToken = createSelector(
  selectAuthState,
  (auth) => auth.shareToken
);

// IS SESSION LOADED
export const selectIsSessionInitializing = createSelector(
  selectAuthState,
  (auth) => auth.isSessionInitializing
);

// IS SESSION LOADED
export const selectIsSessionLoaded = createSelector(
  selectAuthState,
  (auth) => auth.isSessionLoaded
);

// IS USER AUTHENTICATED
export const selectIsAuthenticated = createSelector(
  selectAuthState,
  (auth) => auth.isAuthenticated
);

/**
 * STAFF-3. A counselor or admin signed in with a password, on a live session:
 * the flag alone is not enough, it has to come with a loaded session and its
 * token. Read ONLY by the header's signed-in display and the home page's
 * shared report band. Every member feature (Via, saving, surveys, the front
 * door, notifications) keeps the member check, so a staff session never acts
 * as a member.
 */
export const selectIsStaffSignedIn = createSelector(
  selectAuthState,
  (auth) =>
    !!auth?.isAuthenticated &&
    !!auth.isSessionLoaded &&
    !!auth.token &&
    (auth.role === UserRole.Councelor ||
      auth.role === UserRole.CouncelorAdmin ||
      auth.role === UserRole.SuperAdmin)
);

export const selectIsOverPasscode = createSelector(selectAuthState, (auth) =>
  environment.envName === 'PROD' ? true : auth.isOverPasscode
);

// GET USERNAME
export const selectUsername = createSelector(
  selectAuthState,
  (auth) => auth.username
);

export const selectFirstName = createSelector(
  selectAuthState,
  (auth) => auth.firstName
);

export const selectProfile = createSelector(selectAuthState, (auth) => {
  return {
    firstName: auth.firstName,
    lastName: auth.lastName,
    userEmail: auth.userEmail,
    userRole: auth.role,
    userId: auth.userId,
    status: UserActionEnum.Active
  } as Member;
});

export const selectIsCouncelor = createSelector(
  selectAuthState,
  (auth) => auth.role === UserRole.Councelor
);

// Counselor-or-above: the set allowed on the analytics dashboard. Mirrors the
// backend allowlist on getReportEmbedConfig (powerbi.resolver.ts) so the route
// guard never admits a role the server would reject with a blank embed.
export const selectIsCounselorOrAbove = createSelector(
  selectAuthState,
  (auth) =>
    auth.role === UserRole.Councelor ||
    auth.role === UserRole.CouncelorAdmin ||
    auth.role === UserRole.SuperAdmin
);

// IS ADMIN
export const selectIsAdmin = createSelector(
  selectAuthState,
  (auth) =>
    auth.role === UserRole.CouncelorAdmin || auth.role === UserRole.SuperAdmin
);

export const selectIsCounselorAdmin = createSelector(
  selectAuthState,
  (auth) => auth.role === UserRole.CouncelorAdmin
);

export const selectIsSuperAdmin = createSelector(
  selectAuthState,
  (auth) => auth.role === UserRole.SuperAdmin
);

export const selectCurrentUser = createSelector(
  selectAuthState,
  (auth) => auth.userId
);

// LOGIN WALL REDIRECT URL
export const selectLoginWallRedirectUrl = createSelector(
  selectAuthState,
  (auth) => auth.loginWallRedirectUrl
);

// RETURN SESSION RESULT
export const selectReturnSessionResult = createSelector(
  selectAuthState,
  (auth) => auth.returnSessionResult
);

// ERROR MESSAGE
export const selectErrorMessage = createSelector(
  selectAuthState,
  (auth) => auth.errorMessage
);
